import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import SEOHead from '../../components/SEO/SEOHead';
import SEOFooter from '../../components/SEO/SEOFooter';
import { ArrowLeft, Search } from 'lucide-react';

const BrasileiraoPage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const serieATeams = [
    { 
      rank: 1,
      name: 'Flamengo', 
      titles: 8, 
      region: 'RJ', 
      homeAdvantage: 72, 
      rivalry: 'Fluminense', 
      style: 'Ofensivo',
      tier: 'elite',
      points: 68,
      recentForm: ['W', 'W', 'D', 'W', 'L']
    },
    { 
      rank: 2,
      name: 'Palmeiras', 
      titles: 11, 
      region: 'SP', 
      homeAdvantage: 69, 
      rivalry: 'Corinthians', 
      style: 'Equilibrado',
      tier: 'elite',
      points: 66,
      recentForm: ['W', 'W', 'W', 'D', 'W']
    },
    { 
      rank: 3,
      name: 'São Paulo', 
      titles: 6, 
      region: 'SP', 
      homeAdvantage: 65, 
      rivalry: 'Corinthians', 
      style: 'Defensivo',
      tier: 'elite',
      points: 63,
      recentForm: ['L', 'W', 'W', 'W', 'D']
    },
    { 
      rank: 4,
      name: 'Corinthians', 
      titles: 7, 
      region: 'SP', 
      homeAdvantage: 71, 
      rivalry: 'Palmeiras', 
      style: 'Físico',
      tier: 'contenders',
      points: 59,
      recentForm: ['D', 'D', 'W', 'L', 'W']
    },
    { 
      rank: 5,
      name: 'Santos', 
      titles: 8, 
      region: 'SP', 
      homeAdvantage: 63, 
      rivalry: 'São Paulo', 
      style: 'Técnico',
      tier: 'contenders',
      points: 57,
      recentForm: ['W', 'L', 'D', 'W', 'W']
    },
    { 
      rank: 6,
      name: 'Atlético-MG', 
      titles: 3, 
      region: 'MG', 
      homeAdvantage: 74, 
      rivalry: 'Cruzeiro', 
      style: 'Ofensivo',
      tier: 'contenders',
      points: 55,
      recentForm: ['W', 'W', 'L', 'L', 'W']
    },
    { 
      rank: 7,
      name: 'Internacional', 
      titles: 3, 
      region: 'RS', 
      homeAdvantage: 76, 
      rivalry: 'Grêmio', 
      style: 'Equilibrado',
      tier: 'wildcard',
      points: 52,
      recentForm: ['D', 'W', 'D', 'W', 'L']
    },
    { 
      rank: 8,
      name: 'Grêmio', 
      titles: 2, 
      region: 'RS', 
      homeAdvantage: 73, 
      rivalry: 'Internacional', 
      style: 'Técnico',
      tier: 'wildcard',
      points: 51,
      recentForm: ['L', 'L', 'W', 'W', 'D']
    }
  ];

  const rivalries = [
    {
      name: 'Fla-Flu',
      teams: 'Flamengo vs Fluminense',
      stadium: 'Maracanã',
      avgGoals: 2.8,
      avgCards: 4.2,
      intensity: 'Altíssima',
      historicalWins: { home: 142, away: 138, draws: 89 }
    },
    {
      name: 'Choque-Rei',
      teams: 'São Paulo vs Palmeiras',
      stadium: 'Morumbi/Allianz',
      avgGoals: 2.1,
      avgCards: 5.8,
      intensity: 'Extrema',
      historicalWins: { home: 111, away: 109, draws: 106 }
    },
    {
      name: 'Grenal',
      teams: 'Grêmio vs Internacional',
      stadium: 'Arena/Beira-Rio',
      avgGoals: 2.3,
      avgCards: 6.1,
      intensity: 'Máxima',
      historicalWins: { home: 132, away: 128, draws: 142 }
    },
    {
      name: 'Clássico Mineiro',
      teams: 'Atlético-MG vs Cruzeiro',
      stadium: 'Arena MRV/Mineirão',
      avgGoals: 2.5,
      avgCards: 5.3,
      intensity: 'Alta',
      historicalWins: { home: 158, away: 152, draws: 121 }
    }
  ];

  const tacticalFactors = [
    {
      aspect: 'Mando de Campo',
      impact: 'Altíssimo',
      percentage: 68,
      description: 'Fator casa no Brasil é dos mais fortes do mundo',
      color: 'emerald'
    },
    {
      aspect: 'Viagens Longas',
      impact: 'Crítico',
      percentage: -32,
      description: 'Voos 3+ horas afetam muito o desempenho',
      color: 'red'
    },
    {
      aspect: 'Altitude',
      impact: 'Moderado',
      percentage: 15,
      description: 'Times do planalto têm vantagem em casa',
      color: 'blue'
    },
    {
      aspect: 'Clima',
      impact: 'Significativo',
      percentage: 20,
      description: 'Temperatura altera ritmo do jogo',
      color: 'amber'
    }
  ];

  const faqs = [
    {
      q: "Por que o Brasileirão é tão imprevisível?",
      a: "O Brasileirão tem a menor diferença de nível entre times no mundo. Enquanto na Premier League a diferença entre 1º e 20º é de ~60 pontos, no Brasil raramente passa de 45."
    },
    {
      q: "Qual o melhor momento para apostar?",
      a: "Primeiras 10 rodadas (times se ajustando), últimas 8 rodadas (pressão extrema), jogos pós-Copa e clássicos regionais."
    },
    {
      q: "Como a IA analisa peculiaridades regionais?",
      a: "Nossa IA tem modelos específicos para cada região, considerando viagens, clima, altitude e estilos regionais de jogo."
    },
    {
      q: "A Série B oferece boas oportunidades?",
      a: "Sim! A Série B tem 60% mais zebras que a Série A, odds infladas e menos análise das casas de apostas."
    }
  ];

  const TIER_STYLES = {
    elite: 'border-l-4 border-l-emerald-500',
    contenders: 'border-l-4 border-l-blue-500',
    wildcard: 'border-l-4 border-l-purple-500',
    underperformers: 'border-l-4 border-l-orange-500'
  };

  const TIER_LABELS = {
    all: 'Todos',
    elite: 'Elite',
    contenders: 'Competidores',
    wildcard: 'Imprevisíveis',
    underperformers: 'Em Baixa'
  };

  const filteredTeams = serieATeams.filter(team => {
    const matchesCategory = selectedCategory === 'all' || team.tier === selectedCategory;
    const matchesSearch = team.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const tierCounts = serieATeams.reduce((acc, team) => {
    acc[team.tier] = (acc[team.tier] || 0) + 1;
    return acc;
  }, {});

  // Structured Data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Palpites Brasileirão 2024 com IA | Análise Série A e B",
    "description": "Guia completo de apostas do Campeonato Brasileiro 2024 com análise de Inteligência Artificial. Palpites certeiros, análise tática e estratégias.",
    "author": {
      "@type": "Organization",
      "name": "PickStarAI"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PickStarAI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pickstarai.com/logo.png"
      }
    },
    "datePublished": "2025-08-22",
    "dateModified": new Date().toISOString(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pickstarai.com/brasileirao"
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <>
      <SEOHead
        title="Palpites Brasileirão 2024 com IA | Análise Série A e B Grátis"
        description="Palpites certeiros do Campeonato Brasileiro 2024 com Inteligência Artificial. Análise completa Série A e B, clássicos, zebras e estratégias de apostas."
        keywords="palpites brasileirão, brasileirão 2024, campeonato brasileiro, apostas série a, série b, palpites futebol, análise ia futebol, apostas esportivas brasil, flamengo, palmeiras, corinthians"
        canonical="https://pickstarai.com/brasileirao"
        ogImage="https://pickstarai.com/og-brasileirao.jpg"
        structuredData={[structuredData, faqStructuredData]}
      />

      <div className="min-h-screen bg-black">
        {/* Terminal Header */}
        <header className="fixed top-0 z-50 w-full bg-black border-b border-white/[0.05]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14">
              <Link
                to="/"
                className="flex items-center text-white/60 hover:text-white/90 transition-colors text-[11px] uppercase tracking-wider"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-2" />
                Início
              </Link>
              <div className="text-[9px] text-white/40 uppercase tracking-widest font-mono">
                ATUALIZADO: {new Date().toLocaleDateString('pt-BR').toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section - Terminal */}
        <div className="pt-14 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent pointer-events-none" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 relative">
            <div className="text-center">
              <div className="text-[9px] text-emerald-400/60 uppercase tracking-[0.2em] font-mono mb-4">
                Campeonato Brasileiro Série A
              </div>
              <h1 className="text-5xl lg:text-7xl font-thin text-white/95 tracking-tight mb-6">
                Análise Brasileirão 2024
              </h1>
              <p className="text-base lg:text-lg text-white/50 max-w-2xl mx-auto leading-relaxed">
                Análise completa de todos os times da Série A. Previsões baseadas em inteligência artificial com precisão profissional.
              </p>
            </div>

            {/* Search Bar - Terminal Glass */}
            <div className="mt-12 max-w-md mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/30 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Buscar times..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-lg text-sm text-white/90 placeholder-white/30 focus:outline-none focus:border-emerald-500/50 focus:bg-white/[0.05] transition-all"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16">

          {/* Stats Grid - Terminal Monospace */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-12">
            <div className="bg-white/[0.02] border border-white/[0.05] rounded-lg p-6 text-center group hover:border-white/10 transition-all">
              <div className="text-3xl font-mono font-bold text-emerald-400">Pro</div>
              <div className="text-[10px] text-white/40 uppercase tracking-widest mt-2">Precisão</div>
            </div>
            <div className="bg-white/[0.02] border border-white/[0.05] rounded-lg p-6 text-center group hover:border-white/10 transition-all">
              <div className="text-3xl font-mono font-bold text-emerald-400">8k+</div>
              <div className="text-[10px] text-white/40 uppercase tracking-widest mt-2">Jogos</div>
            </div>
            <div className="bg-white/[0.02] border border-white/[0.05] rounded-lg p-6 text-center group hover:border-white/10 transition-all">
              <div className="text-3xl font-mono font-bold text-emerald-400">50k+</div>
              <div className="text-[10px] text-white/40 uppercase tracking-widest mt-2">Usuários</div>
            </div>
            <div className="bg-white/[0.02] border border-white/[0.05] rounded-lg p-6 text-center group hover:border-white/10 transition-all">
              <div className="text-3xl font-mono font-bold text-emerald-400">24/7</div>
              <div className="text-[10px] text-white/40 uppercase tracking-widest mt-2">Análise</div>
            </div>
          </div>

          {/* Category Filter - Terminal Pills */}
          <div className="mb-8 md:mb-12">
            <div className="flex gap-2 overflow-x-auto scrollbar-hide">
              {Object.entries(TIER_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`px-4 py-2 rounded-full text-[11px] uppercase tracking-wider font-medium transition-all whitespace-nowrap ${
                    selectedCategory === key
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'bg-white/[0.02] text-white/50 border border-white/[0.05] hover:border-white/10 hover:text-white/70'
                  }`}
                >
                  {label} {key !== 'all' && `(${tierCounts[key] || 0})`}
                </button>
              ))}
            </div>
          </div>

          {/* Teams Ranking - Terminal Glass Cards */}
          <div className="space-y-3 mb-16">
            {filteredTeams.map((team, index) => (
              <motion.div
                key={team.name}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.02 }}
                className={`bg-white/[0.02] border border-white/[0.05] rounded-lg p-4 md:p-6 hover:border-white/10 transition-all ${TIER_STYLES[team.tier]}`}
              >
                {/* Mobile Layout */}
                <div className="md:hidden">
                  <div className="flex items-start gap-3">
                    {/* Rank */}
                    <div className="flex flex-col items-center min-w-[45px]">
                      <span className="text-2xl font-mono font-bold text-white/90">
                        {team.rank}
                      </span>
                      <span className="text-[9px] text-white/30 uppercase tracking-wider font-mono">
                        {team.points} pts
                      </span>
                    </div>

                    {/* Team Info - Mobile */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-white/90">
                          {team.name}
                        </h3>
                        <span className="text-xs text-white/40">
                          {team.region}
                        </span>
                      </div>
                      <div className="flex gap-1.5 mt-2">
                        {team.recentForm.map((result, i) => (
                          <span
                            key={i}
                            className={`text-[10px] font-medium font-mono px-1.5 py-0.5 rounded ${
                              result === 'W' ? 'bg-emerald-500/20 text-emerald-400' :
                              result === 'D' ? 'bg-white/[0.05] text-white/50' :
                              'bg-red-500/20 text-red-400'
                            }`}
                          >
                            {result}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Stats - Mobile */}
                    <div className="text-center min-w-[40px]">
                      <div className="text-[9px] text-white/30 uppercase tracking-wider">Casa</div>
                      <div className="text-sm font-mono font-medium text-emerald-400">
                        {team.homeAdvantage}%
                      </div>
                    </div>
                  </div>

                  {/* Extra Info - Below on Mobile */}
                  <div className="mt-3 pt-3 border-t border-white/[0.05]">
                    <div className="flex items-center gap-3 text-[11px] text-white/40">
                      <div>
                        <span className="font-mono">{team.titles}</span>
                        <span className="ml-1">títulos</span>
                      </div>
                      <div>
                        <span>Rival: </span>
                        <span className="text-white/60">{team.rivalry}</span>
                      </div>
                      <div>
                        <span>Estilo: </span>
                        <span className="text-white/60">{team.style}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden md:flex items-start gap-6">
                  {/* Rank */}
                  <div className="flex flex-col items-center min-w-[70px]">
                    <span className="text-4xl font-mono font-bold text-white/90">
                      {team.rank}
                    </span>
                    <span className="text-[10px] text-white/30 uppercase tracking-wider font-mono mt-1">
                      {team.points} pts
                    </span>
                  </div>

                  {/* Team Info */}
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3 mb-3">
                      <h3 className="text-xl font-semibold text-white/90">
                        {team.name}
                      </h3>
                      <span className="text-sm text-white/40">
                        {team.region}
                      </span>
                      <div className="flex gap-1.5">
                        {team.recentForm.map((result, i) => (
                          <span
                            key={i}
                            className={`text-[11px] font-medium font-mono px-2 py-1 rounded ${
                              result === 'W' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20' :
                              result === 'D' ? 'bg-white/[0.05] text-white/50 border border-white/[0.05]' :
                              'bg-red-500/20 text-red-400 border border-red-500/20'
                            }`}
                          >
                            {result}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-white/40">
                      <div>
                        <span className="font-mono text-white/60">{team.titles}</span>
                        <span className="ml-1">títulos</span>
                      </div>
                      <div>
                        <span>Rival: </span>
                        <span className="text-white/60">{team.rivalry}</span>
                      </div>
                      <div>
                        <span>Estilo: </span>
                        <span className="text-white/60">{team.style}</span>
                      </div>
                    </div>
                  </div>

                  {/* Home Advantage */}
                  <div className="text-center min-w-[90px]">
                    <div className="text-[10px] text-white/30 uppercase tracking-wider">Casa</div>
                    <div className="text-2xl font-mono font-bold text-emerald-400 mt-1">
                      {team.homeAdvantage}%
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Rivalries Section - Terminal Glass Cards */}
          <section className="mb-16">
            <div className="text-[10px] text-emerald-400/60 uppercase tracking-[0.2em] font-mono mb-4">
              Grandes Clássicos
            </div>
            <h2 className="text-3xl font-thin text-white/90 mb-8">
              Rivalidades do Brasileirão
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {rivalries.map((rivalry, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.02] border border-white/[0.05] rounded-lg p-6 hover:border-white/10 transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-white/90">{rivalry.name}</h3>
                      <p className="text-sm text-white/50 mt-1">{rivalry.teams}</p>
                    </div>
                    <span className={`text-[10px] font-medium uppercase tracking-wider px-2 py-1 rounded ${
                      rivalry.intensity === 'Máxima' ? 'bg-red-500/20 text-red-400 border border-red-500/20' :
                      rivalry.intensity === 'Extrema' ? 'bg-orange-500/20 text-orange-400 border border-orange-500/20' :
                      rivalry.intensity === 'Altíssima' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/20' :
                      'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20'
                    }`}>
                      {rivalry.intensity}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-white/[0.02] border border-white/[0.05] rounded p-3 text-center">
                      <div className="text-2xl font-mono font-bold text-emerald-400">{rivalry.avgGoals}</div>
                      <div className="text-[10px] text-white/40 uppercase tracking-wider mt-1">Gols/jogo</div>
                    </div>
                    <div className="bg-white/[0.02] border border-white/[0.05] rounded p-3 text-center">
                      <div className="text-2xl font-mono font-bold text-emerald-400">{rivalry.avgCards}</div>
                      <div className="text-[10px] text-white/40 uppercase tracking-wider mt-1">Cartões/jogo</div>
                    </div>
                  </div>

                  <div className="text-[11px] text-white/40 font-mono">
                    <span className="text-white/60">Histórico:</span>
                    <span className="ml-2">V{rivalry.historicalWins.home} E{rivalry.historicalWins.draws} D{rivalry.historicalWins.away}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Tactical Factors - Terminal Design */}
          <section className="mb-16">
            <div className="text-[10px] text-emerald-400/60 uppercase tracking-[0.2em] font-mono mb-4">
              Fatores de Performance
            </div>
            <h2 className="text-3xl font-thin text-white/90 mb-8">
              Fatores Táticos
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {tacticalFactors.map((factor, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.02] border border-white/[0.05] rounded-lg p-6 hover:border-white/10 transition-all"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-white/90">{factor.aspect}</h3>
                    <span className={`text-3xl font-mono font-bold ${
                      factor.percentage > 0 ? 'text-emerald-400' : 'text-red-400'
                    }`}>
                      {factor.percentage > 0 ? '+' : ''}{factor.percentage}%
                    </span>
                  </div>
                  <p className="text-sm text-white/50 mb-4">{factor.description}</p>
                  <div className="w-full bg-white/[0.05] rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full ${
                        factor.color === 'emerald' ? 'bg-emerald-500' :
                        factor.color === 'red' ? 'bg-red-500' :
                        factor.color === 'blue' ? 'bg-blue-500' :
                        'bg-amber-500'
                      }`}
                      style={{ width: `${Math.abs(factor.percentage)}%` }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* AI Analysis - Terminal Glass Card */}
          <section className="mb-16">
            <div className="text-[10px] text-emerald-400/60 uppercase tracking-[0.2em] font-mono mb-4">
              Sistema de Análise
            </div>
            <h2 className="text-3xl font-thin text-white/90 mb-8">
              Como Nossa IA Funciona
            </h2>

            <div className="bg-white/[0.02] border border-white/[0.05] rounded-lg p-8">
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <h3 className="font-semibold text-white/90 mb-4 text-sm uppercase tracking-wider">Dados Técnicos</h3>
                  <ul className="space-y-3 text-sm text-white/50">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400 mt-1">→</span>
                      <span>Posse de bola e finalizações</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400 mt-1">→</span>
                      <span>Passes certos e desarmes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400 mt-1">→</span>
                      <span>Faltas e cartões</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-white/90 mb-4 text-sm uppercase tracking-wider">Contexto do Jogo</h3>
                  <ul className="space-y-3 text-sm text-white/50">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-1">→</span>
                      <span>Histórico de confrontos</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-1">→</span>
                      <span>Forma recente e motivação</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-1">→</span>
                      <span>Lesões e suspensões</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-white/90 mb-4 text-sm uppercase tracking-wider">Fatores Externos</h3>
                  <ul className="space-y-3 text-sm text-white/50">
                    <li className="flex items-start gap-2">
                      <span className="text-violet-400 mt-1">→</span>
                      <span>Clima e altitude</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-violet-400 mt-1">→</span>
                      <span>Distância de viagem</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-violet-400 mt-1">→</span>
                      <span>Árbitro escalado</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-white/[0.05]">
                <p className="text-sm text-white/50">
                  <span className="font-semibold text-white/70 font-mono">Machine Learning:</span> Random Forest + Neural Networks
                  treinadas com 15 anos de dados (8.000+ jogos) para máxima precisão.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section - Terminal Accordion */}
          <section className="mb-16">
            <div className="text-[10px] text-emerald-400/60 uppercase tracking-[0.2em] font-mono mb-4">
              Perguntas Frequentes
            </div>
            <h2 className="text-3xl font-thin text-white/90 mb-8">
              Dúvidas sobre Brasileirão
            </h2>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="bg-white/[0.02] border border-white/[0.05] rounded-lg overflow-hidden group hover:border-white/10 transition-all"
                >
                  <summary className="px-6 py-4 cursor-pointer list-none flex items-center justify-between text-white/80 hover:text-white/95 transition-colors">
                    <span className="text-sm font-medium pr-4">{faq.q}</span>
                    <span className="text-white/40 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <div className="px-6 pb-4 text-sm text-white/50 leading-relaxed border-t border-white/[0.05] pt-4">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* Bottom CTA - Terminal Gradient */}
          <div className="mt-20 text-center">
            <div className="bg-white/[0.02] border border-white/[0.05] rounded-lg p-12">
              <div className="text-[10px] text-emerald-400/60 uppercase tracking-[0.2em] font-mono mb-4">
                Comece Agora
              </div>
              <h2 className="text-3xl font-thin text-white/90 mb-4">
                Análise Profissional do Brasileirão
              </h2>
              <p className="text-base text-white/50 mb-8 max-w-2xl mx-auto leading-relaxed">
                Junte-se a mais de 50.000 apostadores usando nossa IA para dominar o
                campeonato mais imprevisível do mundo.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="px-8 py-3 bg-gradient-to-r from-emerald-500/80 to-violet-500/80 hover:from-emerald-500 hover:to-violet-500 text-white text-sm font-medium rounded-lg transition-all shadow-lg shadow-emerald-500/20"
              >
                Começar Análise Grátis
              </button>
            </div>
          </div>
        </div>

        {/* SEO Footer */}
        <SEOFooter language="pt-BR" />
      </div>
    </>
  );
};

export default BrasileiraoPage;